import { useSyncExternalStore } from "react";
import { preconApi } from "./api";

// One shared unread-notification store: the header bell and every notes icon read the same list.
let state = { items: [], unread: 0 };
const subs = new Set();
let timer = null;
const emit = () => subs.forEach((f) => f());

export const refreshMentions = async () => {
  try {
    const r = await preconApi.notifications();
    state = { items: r.items || [], unread: r.unread || 0 };
    emit();
  } catch { /* signed out or offline — keep the last list */ }
};

const subscribe = (fn) => {
  subs.add(fn);
  if (subs.size === 1) { refreshMentions(); timer = setInterval(refreshMentions, 60000); }
  return () => { subs.delete(fn); if (!subs.size) clearInterval(timer); };
};

export const useMentions = () => useSyncExternalStore(subscribe, () => state);

const matches = (n, t) => n.kind === "mention" && (
  t.contract_id ? n.source === "contract" && n.contract_id === t.contract_id
  : t.field_project_id ? n.source === "field" && n.project_id === t.field_project_id
  : t.project_id ? n.source === "pipeline" && n.project_id === t.project_id
  : t.budget_id ? n.source === "bod" && n.budget_id === t.budget_id && n.scope_code === t.scope_code
  : !["bod", "contract", "pipeline", "field"].includes(n.source) && n.job_id === t.job_id);

export const markAllMentionsRead = async () => {
  state = { items: [], unread: 0 };
  emit();
  try { await preconApi.markNotificationsRead(); } finally { refreshMentions(); }
};

export const unreadFor = (items, target) => items.filter((n) => matches(n, target)).length;

// Opening a row's notes clears its unread mentions.
export const clearMentions = async (target) => {
  if (!state.items.some((n) => matches(n, target))) return;
  const items = state.items.filter((n) => !matches(n, target));
  state = { items, unread: Math.max(0, state.unread - (state.items.length - items.length)) };
  emit();
  try { await preconApi.readNotificationTarget(target); } finally { refreshMentions(); }
};

const rowSelector = (n) => (n.source === "contract" ? `[data-mention-row="contract-${n.contract_id}"]`
  : n.source === "pipeline" ? `[data-mention-row="pipeline-${n.project_id}"]`
  : `[data-testid="${n.source === "bod" ? `spec-row-${n.budget_id}-${n.scope_code}` : `board-row-${n.job_id}`}"]`);
const TAB_OF = { bod: "bod", contract: "contracts", pipeline: "pipeline" };

// Jump to the tab, then wait for the row to render, scroll to it and flash it.
export const jumpToMention = (n) => {
  if (n.source === "field") {   // RA job notes live on their own route — open the job's Notes tab
    window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { path: `/projects/${n.project_id}?tab=notes` } }));
    return;
  }
  window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: TAB_OF[n.source] || "bidboard" } }));
  const sel = rowSelector(n);
  let tries = 0;
  const seek = setInterval(() => {
    const el = document.querySelector(sel);
    if (el || ++tries > 40) clearInterval(seek);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.classList.add("mention-flash");
    setTimeout(() => el.classList.remove("mention-flash"), 2600);
  }, 150);
};
